import { EcEnv } from '@castlery/config';
import type { DataBucketStoryblok } from '@castlery/types';
import type { ISbStoryData, ISbStoryParams } from '@storyblok/react';
import { logger } from '@castlery/observability';
import { unstable_cache } from 'next/cache';
/**
 * 内部函数：实际执行PDP数据桶获取逻辑
 * 这个函数会被 unstable_cache 包裹以提供缓存功能
 */
async function _getPdpDataBucketServerInternal(
  slug: string,
  params?: Partial<ISbStoryParams>
): Promise<ISbStoryData<DataBucketStoryblok> | null> {
  // 构建API URL相关信息（提前定义以便在catch中使用）
  const region = EcEnv.NEXT_PUBLIC_COUNTRY.toLowerCase();
  const path = `${region}/general-configuration/universal-config-new-joyboy/pdp/data-bucket/${slug}`;

  try {
    const token = EcEnv.NEXT_PUBLIC_STORYBLOK_TOKEN;
    const version = EcEnv.NEXT_PUBLIC_APPLICATION_ENV.includes('test') ? 'draft' : 'published';

    // 构建请求URL和参数
    let url = `https://api.storyblok.com/v2/cdn/stories/${path}?token=${token}&version=${version}`;

    // 添加其他查询参数
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url += `&${key}=${encodeURIComponent(String(value))}`;
        }
      });
    }

    // 执行请求（不使用 Next.js fetch 缓存，因为我们用 unstable_cache 处理）
    const response = await fetch(url, {
      cache: 'no-store',
    });

    if (!response.ok) {
      // 404 是正常情况（产品未配置数据桶），不记录日志，静默处理
      if (response.status === 404) {
        return null;
      }

      // 其他 4xx 客户端错误使用 warn 级别
      if (response.status >= 400 && response.status < 500) {
        logger.warn('PDPDataBucket client error', { status: response.status, slug, path });
      }
      // 5xx 服务器错误使用 error 级别
      else if (response.status >= 500) {
        logger.error('Failed to fetch PDPDataBucket', { status: response.status, slug, path });
      }

      return null;
    }

    const data = await response.json();
    return data.story;
  } catch (error) {
    // 网络错误或解析错误才记录 error
    // logger.error('Error fetching PDPDataBucket', { error, slug, path });
    return null;
  }
}

/**
 * 服务端获取PDP数据桶内容（带缓存）
 *
 * @param slug - PDP数据的slug标识符
 * @param params - Storyblok查询参数
 * @param cacheOptions - 缓存配置选项
 * @returns 返回Storyblok故事数据
 */
export async function getPdpDataBucketServer(
  slug: string,
  params?: Partial<ISbStoryParams>,
  cacheOptions?: {
    tags?: string[];
    revalidate?: number;
  }
): Promise<ISbStoryData<DataBucketStoryblok> | null> {
  // 构建缓存键，包含所有影响结果的参数
  const cacheKey = `pdp-data-bucket-${slug}${params ? `-${JSON.stringify(params)}` : ''}`;

  // 默认缓存配置
  const defaultCacheConfig = {
    tags: ['pdp-data-bucket'],
    revalidate: 600, // 10分钟缓存，包括404响应
  };

  // 合并用户提供的缓存配置
  const finalCacheConfig = {
    ...defaultCacheConfig,
    ...(cacheOptions?.tags && { tags: [...defaultCacheConfig.tags, ...cacheOptions.tags] }),
    ...(cacheOptions?.revalidate !== undefined && { revalidate: cacheOptions.revalidate }),
  };

  // 动态创建缓存函数
  const cachedFunction = unstable_cache(_getPdpDataBucketServerInternal, [cacheKey], finalCacheConfig);

  return cachedFunction(slug, params);
}
